// src/components/DisciplineBoardPanel.js
//
// T5 / D-20 (2026-10-15) - THE PATH TO ARBITRATION FROM THE DISCIPLINE CASE. arbitrationCases.js has modelled a
// discipline Board case (caseType 'discipline') since 2026-08-27 and no page used it: the docket was a record, not a
// workflow. This panel lets the General Chairperson docket THIS case before a Board - the Board case is created FROM
// the case (linkedDisciplineCaseId), validated by arbitrationCases.validateLiveCase, under the existing
// arbitrationCases rules (GC of the committee or admin creates; identity fields pinned; never deleted). Officers who
// read the case see the Board case(s) docketed from it.
import React, { useEffect, useState } from 'react';
import { db, auth } from '../firebase';
import { addDoc, collection, onSnapshot, query, serverTimestamp, where } from 'firebase/firestore';
import { validateLiveCase, submissionClock } from '../data/arbitrationCases';

const C = { line: '#2E4A6B', dim: '#8AA4BF', text: '#DCE7F2', ok: '#5FB878', bad: '#E06C6C', accent: '#E8A020' };
const BOARDS = ['NRAB', 'PLB', 'SBA'];

function linkedQuery(k, user) {
  const col = collection(db, 'arbitrationCases');
  if (!user) return null;
  if (user.isAdmin || user.role === 'admin') return query(col, where('linkedDisciplineCaseId', '==', k.id));
  if (user.role === 'general_chair' && user.committee && user.committee === k.generalCommittee)
    return query(col, where('committee', '==', user.committee), where('linkedDisciplineCaseId', '==', k.id));
  if (user.role === 'local_chair' && k.scopeKey && Array.isArray(user.scopes) && user.scopes.indexOf(k.scopeKey) !== -1)
    return query(col, where('scopeKey', '==', k.scopeKey), where('linkedDisciplineCaseId', '==', k.id));
  return null;
}

export default function DisciplineBoardPanel({ disciplineCase, user }) {
  const k = disciplineCase || {};
  const [cases, setCases] = useState([]);
  const [loadErr, setLoadErr] = useState('');
  const [form, setForm] = useState({ board: 'NRAB', division: '', docketNumber: '', submissionDue: '', noticeOfIntentDate: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const q = k.id ? linkedQuery(k, user) : null;

  useEffect(() => {
    if (!q) return undefined;
    return onSnapshot(q, snap => { setCases(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoadErr(''); },
      e => setLoadErr((e && (e.code || e.message)) || 'refused'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [k.id, user && user.uid, user && user.role]);

  if (!q || !k.status || k.status === 'draft') return null;
  const isGC = !!user && ((user.role === 'general_chair' && user.committee === k.generalCommittee) || user.isAdmin || user.role === 'admin');
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const today = new Date().toISOString().slice(0, 10);

  const docket = async () => {
    const rec = {
      recordClass: 'LIVE', caseType: 'discipline', status: 'open',
      committee: String(k.generalCommittee || ''), scopeKey: String(k.scopeKey || ''),
      board: form.board.trim(), division: form.division.trim(), docketNumber: form.docketNumber.trim(),
      submissionDue: form.submissionDue, noticeOfIntentDate: form.noticeOfIntentDate || null,
      linkedDisciplineCaseId: k.id, linkedClaimId: '', carrierClaimNumber: k.caseNumber || null,
      claimant: String(k.memberName || ''), carrier: String(k.railroad || ''),
    };
    const v = validateLiveCase(rec);
    if (!v.ok) { setMsg({ ok: false, text: v.errors.join(' ') }); return; }
    setBusy(true); setMsg(null);
    try {
      await addDoc(collection(db, 'arbitrationCases'), { ...rec, createdBy: (auth.currentUser && auth.currentUser.uid) || '', createdAt: serverTimestamp() });
      setMsg({ ok: true, text: 'Docketed before the ' + rec.board + ' as ' + rec.docketNumber + '.' });
      setForm({ board: 'NRAB', division: '', docketNumber: '', submissionDue: '', noticeOfIntentDate: '' });
    } catch (e) { setMsg({ ok: false, text: (e && e.message) || 'Not docketed.' }); }
    setBusy(false);
  };

  return (
    <div style={{ border: '1px solid ' + C.line, padding: '10px 14px', margin: '12px 0' }}>
      <div style={{ color: C.text, fontWeight: 700, fontSize: 13 }}>Before a Board - arbitration</div>
      <div style={{ color: C.dim, fontSize: 11, margin: '2px 0 8px' }}>
        A Board case docketed from this discipline case. The General Chairperson dockets it; the division and docket number are the Board's, as its notice or acknowledgement gives them.
      </div>
      {loadErr ? <div style={{ color: C.bad, fontSize: 12 }}>Board cases could not be loaded ({loadErr}). This is a load failure, not "none".</div>
        : cases.length === 0 ? <div style={{ color: C.dim, fontSize: 12 }}>This case has not been docketed before a Board.</div>
        : cases.map(b => {
          const clk = submissionClock(b, today) || {};
          return (
            <div key={b.id} style={{ fontSize: 12, color: C.text, padding: '4px 0', borderBottom: '1px dotted ' + C.line }}>
              <b>{b.board}</b> {b.division ? '- ' + b.division : ''} - docket {b.docketNumber} - submission due {b.submissionDue}
              {b.submissionFiledDate ? <span style={{ color: C.ok }}> - filed {b.submissionFiledDate}</span>
                : clk.daysRemaining !== undefined && clk.daysRemaining !== null ? <span style={{ color: clk.daysRemaining < 0 ? C.bad : clk.daysRemaining <= 21 ? C.accent : C.dim }}> ({clk.daysRemaining}d)</span> : null}
            </div>
          );
        })}
      {isGC ? (
        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <select aria-label="Which Board the case goes to" className="form-select" value={form.board} onChange={e => set('board', e.target.value)}>{BOARDS.map(b => <option key={b} value={b}>{b}</option>)}</select>
          <input aria-label="Division (as the Board set it)" className="form-input" placeholder="Division (as the Board set it)" value={form.division} onChange={e => set('division', e.target.value)} />
          <input aria-label="Docket number" className="form-input" placeholder="Docket number" value={form.docketNumber} onChange={e => set('docketNumber', e.target.value)} />
          <label style={{ fontSize: 11, color: C.dim }}>Submission due <input aria-label="Submission due" className="form-input" type="date" value={form.submissionDue} onChange={e => set('submissionDue', e.target.value)} /></label>
          <label style={{ fontSize: 11, color: C.dim }}>Notice of intent <input aria-label="Submission due" className="form-input" type="date" value={form.noticeOfIntentDate} onChange={e => set('noticeOfIntentDate', e.target.value)} /></label>
          <button className="btn btn-primary" disabled={busy} onClick={docket}>{busy ? 'Docketing...' : 'Docket before the Board'}</button>
          {msg ? <span style={{ fontSize: 12, color: msg.ok ? C.ok : C.bad }}>{msg.text}</span> : null}
        </div>
      ) : null}
    </div>
  );
}
